import { evaluateCondition } from './forms.js';
/**
 * Dossiers de candidature (cahier §5.3, §7, TODO phase 9, ADR-024).
 * Partagé entre l'API (règles) et les portails (libellés, frise, contrôles).
 */
export const APPLICATION_STATE_LABELS = {
    draft: 'Brouillon',
    ready: 'Prêt à soumettre',
    submitted: 'Soumis',
    under_review: 'En vérification',
    to_complete: 'À compléter',
    admissible: 'Recevable',
    not_admissible: 'Non recevable',
    withdrawn: 'Retiré',
    cancelled: 'Annulé',
};
/** Brouillon modifiable par le candidat (avant le dépôt officiel). */
export const APPLICATION_EDITABLE_STATES = ['draft', 'ready'];
/** Dossier qui ne compte plus : le candidat peut en recréer un pour le même concours. */
export const APPLICATION_INACTIVE_STATES = ['withdrawn', 'cancelled'];
/** Dossier déposé (contenu verrouillé, récépissé disponible). */
export const APPLICATION_SUBMITTED_STATES = [
    'submitted',
    'under_review',
    'to_complete',
    'admissible',
    'not_admissible',
];
/** Étapes affichées dans la frise de suivi (cahier §7.1). */
export const APPLICATION_TIMELINE = [
    'draft',
    'submitted',
    'under_review',
    'admissible',
];
export const PAYMENT_STATE_LABELS = {
    not_required: 'Non requis',
    pending: 'En attente',
    processing: 'En cours',
    confirmed: 'Confirmé',
    failed: 'Échoué',
    cancelled: 'Annulé',
    refunded: 'Remboursé',
};
/** État de paiement initial d'un dossier (états séparés de ceux du dossier, §7.1). */
export function initialPaymentState(timing, feeAmount) {
    return timing === 'not_required' || feeAmount <= 0 ? 'not_required' : 'pending';
}
/** Âge révolu à une date donnée (AAAA-MM-JJ). */
export function ageAt(birthDate, at) {
    const [by, bm, bd] = birthDate.split('-').map(Number);
    const [ay, am, ad] = at.split('-').map(Number);
    let age = ay - by;
    if (am < bm || (am === bm && ad < bd))
        age--;
    return age;
}
export function ageReferenceDate(eligibility, year) {
    return eligibility.ageReferenceDate ?? `${year}-12-31`;
}
const frDate = (iso) => iso.split('-').reverse().join('/');
/**
 * Contrôles d'éligibilité automatisables. Le portail les affiche (guidage),
 * l'API les rejoue à la soumission (décision).
 */
export function checkEligibility(input) {
    const issues = [];
    if (input.missingProfileFields.length)
        issues.push({
            code: 'profile_incomplete',
            message: `Profil incomplet : ${input.missingProfileFields.join(', ')}.`,
        });
    if (!input.emailVerified)
        issues.push({ code: 'email_unverified', message: 'Adresse électronique non vérifiée.' });
    const { minAge, maxAge } = input.eligibility;
    if (minAge != null || maxAge != null) {
        const ref = ageReferenceDate(input.eligibility, input.year);
        if (!input.birthDate) {
            if (!input.missingProfileFields.length)
                issues.push({ code: 'birth_date_missing', message: 'Date de naissance non renseignée.' });
        }
        else {
            const age = ageAt(input.birthDate, ref);
            if (minAge != null && age < minAge)
                issues.push({
                    code: 'too_young',
                    message: `Âge minimum requis : ${minAge} ans au ${frDate(ref)} (vous aurez ${age} ans).`,
                });
            if (maxAge != null && age > maxAge)
                issues.push({
                    code: 'too_old',
                    message: `Âge maximum autorisé : ${maxAge} ans au ${frDate(ref)} (vous aurez ${age} ans).`,
                });
        }
    }
    return issues;
}
/* ------------------------------------------------------------------ */
/* Corrections après dépôt (ADR-013)                                   */
/* ------------------------------------------------------------------ */
export const CORRECTION_STATUSES = {
    pending: 'En attente de validation',
    approved: 'Validée',
    rejected: 'Refusée',
};
/** Référence de dépôt / vérification : 10 caractères de l'alphabet des matricules. */
export const SUBMISSION_REF_LENGTH = 10;
const isoDate = /^\d{4}-\d{2}-\d{2}$/;
/** Valeur d'un champ telle qu'un humain la lit (libellés d'options, dates JJ/MM/AAAA…). */
export function formatAnswer(field, value) {
    if (value === null || value === undefined || value === '')
        return null;
    const option = (v) => field.options?.find((o) => o.value === v)?.label ?? String(v);
    switch (field.type) {
        case 'select':
        case 'radio':
            return option(value);
        case 'multiselect':
            return Array.isArray(value) && value.length ? value.map(option).join(', ') : null;
        case 'checkbox':
            return value === true ? 'Oui' : 'Non';
        case 'date':
            return typeof value === 'string' && isoDate.test(value) ? frDate(value) : String(value);
        case 'file':
        case 'image': {
            const file = value;
            return file?.name ?? 'Pièce jointe';
        }
        default: {
            const text = typeof value === 'number' ? String(value).replace('.', ',') : String(value);
            return field.unit ? `${text} ${field.unit}` : text;
        }
    }
}
/** Sections et champs VISIBLES pour ces réponses, dans l'ordre du formulaire. */
export function buildSummary(schema, values) {
    const out = [];
    for (const section of schema.sections) {
        if (!evaluateCondition(section.visibility, values))
            continue;
        const items = section.fields
            .filter((f) => evaluateCondition(f.visibility, values))
            .map((f) => ({ key: f.key, label: f.label, value: formatAnswer(f, values[f.key]) }));
        if (items.length)
            out.push({ key: section.key, title: section.title, items });
    }
    return out;
}
/* ------------------------------------------------------------------ */
/* Pré-remplissage depuis le profil                                    */
/* ------------------------------------------------------------------ */
/**
 * Clés de formulaire conventionnelles pré-remplies depuis le profil candidat à
 * la création du brouillon (le candidat peut ensuite les modifier). Évite de
 * ressaisir l'identité dans chaque dossier.
 */
export const PROFILE_PREFILL = {
    nom: 'lastName',
    prenoms: 'firstName',
    date_naissance: 'birthDate',
    lieu_naissance: 'birthPlace',
    sexe: 'gender',
    nationalite: 'nationality',
    telephone: 'phone',
    adresse: 'address',
    ville: 'city',
    region: 'region',
};
//# sourceMappingURL=applications.js.map